import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

type QueryKey = string[];

/**
 * A custom hook to watch for API slice changes and memoize a selector result.
 *
 * This hook should be used only in edge cases and for creating abstractions.
 * For general usage, hooks exported from slices should be preferred.
 *
 * @template T The type of the value returned by the selector function.
 *
 * @param {() => T} selector - A function that selects and returns data from the API state.
 * @param {QueryKey[]} dependencyArray - An array of query keys representing the API slices to watch.
 *                                       Each key is an array of strings.
 *
 * @returns {T} The memoized result of the selector function.
 *
 * @example
 * const userDataSelector = () => ({
 *   name: getApiSlice(['user', 'profile'])?.name,
 *   email: getApiSlice(['user', 'profile'])?.email
 * });
 *
 * const userData = useApiSliceSelector(userDataSelector, [['user', 'profile']]);
 *
 * @example
 * // Watching multiple slices
 * const dashboardDataSelector = () => ({
 *   userCount: getApiSlice(['stats', 'users'])?.count,
 *   revenueTotal: getApiSlice(['stats', 'revenue'])?.total
 * });
 *
 * const dashboardData = useApiSliceSelector(
 *   dashboardDataSelector,
 *   [['stats', 'users'], ['stats', 'revenue']]
 * );
 *
 * The selector function is memoized based on the stringified result of the queries,
 * which means it will only re-run when the watched slices change.
 *
 */
export const useApiSliceSelector = <T>(selector: () => T, dependencyArray: QueryKey[]): T => {
    const queryOptions = useMemo(() => {
        return {
            context: undefined,
            queries: dependencyArray
                .filter((dep) => !!dep.length)
                .map((dep) => ({
                    queryKey: dep,
                    enabled: false,
                })),
        };
    }, [JSON.stringify(dependencyArray)]);
    const result = useQueries(queryOptions);

    return useMemo(() => selector(), [JSON.stringify(result)]);
};
